import axios from 'axios'
import _get from 'lodash/get'

import { saveProfile } from '@/store/profile'

import {
    ERROR_MESSAGE_DELETING,
    ERROR_MESSAGE_SAVING,
    TAX_REGISTRATION_STATUS_STARTED,
    TAX_REGISTRATION_STATUS_SUBMITTED,
    USER_AGREEMENT_TYPE_TAX_REGISTRATION,
    VARIANT_UTR,
} from '../constants'
import { track, TRACK_TAX_REGISTRATION_START, TRACK_TAX_REGISTRATION_SUBMIT } from '../trackers'
import { receiveSuccessAlert } from './alerts'
import { handleRequestError } from './request'

export const ASSIGN_TAX_REGISTRATION = 'ASSIGN_TAX_REGISTRATION'
export const DELETE_TAX_REGISTRATION = 'DELETE_TAX_REGISTRATION'
export const RECEIVE_TAX_REGISTRATION = 'RECEIVE_TAX_REGISTRATION'
export const REQUEST_TAX_REGISTRATION = 'REQUEST_TAX_REGISTRATION'
export const SAVE_TAX_REGISTRATION = 'SAVE_TAX_REGISTRATION'
export const SUBMIT_TAX_REGISTRATION = 'SUBMIT_TAX_REGISTRATION'
export const RESET_TAX_REGISTRATION = 'RESET_TAX_REGISTRATION'
export const RECEIVE_TAX_REGISTRATION_AGREEMENT = 'RECEIVE_TAX_REGISTRATION_AGREEMENT'

export const requestTaxRegistration = () => (dispatch, getState) => {
    dispatch({ type: REQUEST_TAX_REGISTRATION })

    const { profile } = getState()
    const { id: userId } = profile

    return axios
        .get(`/taxregistration/${userId}`)
        .then((response) => {
            const responseData = _get(response, ['data'], {})
            dispatch(receiveTaxRegistration({ ...responseData, fetchSuccessful: true }))
            return responseData
        })
        .catch((error) => {
            if (_get(error, ['response', 'status']) === 404) {
                dispatch(receiveTaxRegistration({ fetchSuccessful: true }))
                return Promise.resolve()
            }

            dispatch(receiveTaxRegistration({ fetchSuccessful: false }))
            dispatch(handleRequestError(error))
            return Promise.reject(error)
        })
}

export const receiveTaxRegistration = (data) => ({ type: RECEIVE_TAX_REGISTRATION, data })

export const resetTaxRegistration = () => ({ type: RESET_TAX_REGISTRATION })

export const saveTaxRegistration =
    (data = {}) =>
    (dispatch, getState) => {
        dispatch({ type: SAVE_TAX_REGISTRATION, data })

        const { profile, taxRegistration } = getState()
        const { id: userId } = profile

        const { taxRegistration: updatedTaxRegistration = {} } = data

        return axios
            .put(`/taxregistration/${userId}`, { ...taxRegistration, ...updatedTaxRegistration, userId })
            .then((response) => {
                const responseData = _get(response, ['data'], {})
                const responseStatus = _get(response, ['status'], {})

                if (responseStatus === 201) {
                    track(TRACK_TAX_REGISTRATION_START)
                }

                dispatch(receiveTaxRegistration(responseData))

                return Promise.resolve(responseData)
            })
            .catch((error) => {
                dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_SAVING }))
                return Promise.reject(error)
            })
    }

export const assignTaxRegistration = () => (dispatch, getState) => {
    dispatch({ type: ASSIGN_TAX_REGISTRATION })

    const { profile } = getState()
    const { id: userId } = profile

    return axios
        .put(`/taxregistration/${userId}/accountant`)
        .then((response) => {
            const responseData = _get(response, ['data'], {})
            dispatch(receiveTaxRegistration(responseData))

            return Promise.resolve(responseData)
        })
        .catch((error) => {
            dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_SAVING }))
            return Promise.reject(error)
        })
}

export const submitTaxRegistration = () => (dispatch, getState) => {
    dispatch({ type: SUBMIT_TAX_REGISTRATION })

    const { profile, taxRegistration } = getState()

    if (taxRegistration?.status === TAX_REGISTRATION_STATUS_STARTED) {
        return dispatch(
            saveTaxRegistration({
                taxRegistration: { ...taxRegistration, status: TAX_REGISTRATION_STATUS_SUBMITTED },
            })
        ).then(() => {
            if (profile?.variant === VARIANT_UTR) {
                dispatch(saveProfile({ variant: null }))
            }

            dispatch(receiveSuccessAlert('Tax registration submitted'))

            track(TRACK_TAX_REGISTRATION_SUBMIT)
        })
    }

    return Promise.resolve()
}

export const deleteTaxRegistration = () => (dispatch, getState) => {
    dispatch({ type: DELETE_TAX_REGISTRATION })

    const { profile } = getState()
    const { id: userId } = profile

    return axios
        .delete(`/taxregistration/${userId}`)
        .then(() => {
            dispatch(resetTaxRegistration())
        })
        .catch((error) => {
            dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_DELETING }))
            return Promise.reject(error)
        })
}

export const createTaxRegistrationAgreement = () => (dispatch, getState) => {
    const { profile, taxRegistration } = getState()

    return axios
        .post(`/useragreements`, {
            accountantId: taxRegistration.accountantId,
            taxRegistrationId: taxRegistration.userId,
            userId: profile.id,
            userAgreementType: USER_AGREEMENT_TYPE_TAX_REGISTRATION,
        })
        .then((response) => {
            const responseData = _get(response, ['data'], {})
            dispatch(receiveTaxRegistrationAgreement({ agreements: [responseData] }))
            dispatch(receiveTaxRegistration({ isMissingAgreement: false }))
            return responseData
        })
        .catch((error) => {
            dispatch(handleRequestError(error))
            return Promise.reject(error)
        })
}

export const requestTaxRegistrationAgreementDraft = () => (dispatch, getState) => {
    const { profile, taxRegistration } = getState()

    return axios
        .post(`/useragreements/draft`, {
            accountantId: taxRegistration.accountantId,
            taxRegistrationId: taxRegistration.userId,
            userId: profile.id,
            userAgreementType: USER_AGREEMENT_TYPE_TAX_REGISTRATION,
        })
        .then((response) => {
            const responseData = _get(response, ['data'], {})
            dispatch(receiveTaxRegistrationAgreement({ agreements: [responseData] }))
            return responseData
        })
        .catch((error) => {
            dispatch(handleRequestError(error))
        })
}

export const requestTaxRegistrationAgreement = () => (dispatch, getState) => {
    const { profile, taxRegistration } = getState()

    return axios
        .get(`/taxregistration/${profile.id}/agreement`, {
            accountantId: taxRegistration.accountantId,
            userId: profile.id,
            userAgreementType: USER_AGREEMENT_TYPE_TAX_REGISTRATION,
        })
        .then((response) => {
            const responseData = _get(response, ['data'], {})
            dispatch(receiveTaxRegistrationAgreement({ agreements: [responseData] }))
            return responseData
        })
        .catch((error) => {
            if (_get(error, ['response', 'status']) === 404) {
                return
            }

            dispatch(handleRequestError(error))
        })
}

const receiveTaxRegistrationAgreement = (data) => ({ type: RECEIVE_TAX_REGISTRATION_AGREEMENT, data })
